import { lazy, Suspense } from 'react';
import { useEPrescriptionStatus } from '@features/eprescriptions';
import { useCanPrepareOnBehalf } from '@features/user';

import type { AddonOnboardingEntry } from './types';

// Heavy modal — lazy-loaded so it only enters the bundle when actually shown
// (keeps it out of the RootView critical path for non-proxy users).
const ProxyOnboarding = lazy(() =>
  import('./components/ProxyOnboarding').then(m => ({ default: m.ProxyOnboarding })),
);

/**
 * Prescribing Proxy onboarding entry.
 *
 * Due when the user has the proxy capability (`can_prepare_prescriptions_on_behalf`
 * + operations_support) but isn't yet registered with DoseSpot (`user_connected`).
 * The status query is skipped for non-proxies, so it costs nothing for everyone else.
 */
const proxyOnboardingEntry: AddonOnboardingEntry = {
  id: 'prescribing-proxy',
  useStatus: () => {
    const isProxy = useCanPrepareOnBehalf();
    const { isUserConnected, isLoading } = useEPrescriptionStatus({ skip: !isProxy });
    return { shouldShow: isProxy && !isUserConnected, isLoading: isProxy && isLoading };
  },
  render: ({ onDone }) => (
    <Suspense fallback={null}>
      <ProxyOnboarding onComplete={onDone} onClose={onDone} />
    </Suspense>
  ),
};

/** Add-on onboardings, in priority order — AddOnOnboardingHost shows one at a time. */
export const ADDON_ONBOARDINGS: AddonOnboardingEntry[] = [proxyOnboardingEntry];
